import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import RichTextEditor from "../../Common/TinyMCE/RichTextEditor";
import "flatpickr/dist/themes/material_green.css";
import { toast } from "react-toastify";
import {
  getArticleById,
  addArticle,
  updateArticle,
} from "../../../services/articlesService";
import { getSectionsList } from "../../../services/sectionsService";
import { getSubSectionsBySectionId } from "../../../services/subSectionsService";
import { getContentTopicsList } from "../../../services/contentTopicsService";
import { validateArticle } from "../../../utils/validation";
import { generateSlug } from "../../../utils/cmsUtils";
import {
  buildArticleFormDataForSubmit,
  mapArticleResponse,
} from "./articleFormUtils";
import { ARTICLE_STATUS } from "./articleConstants";
import { handleErrors } from "../../../utils/errorHandler";
import AiAssistantOffcanvas from "../../AI/AiAssistantOffcanvas";
import { CONTENT_AI_RESPONSE_FIELD_MAP } from "../../AI/AIResponseFieldMap";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useUserProfile } from "../../../hooks/useUserProfile";
import FormSkeleton from "../../Common/Loaders/FormSkeleton";
import ButtonLoader from "../../Common/Loaders/ButtonLoader";
import ImageUpload from "../../Common/ImageUpload/ImageUpload";
import { useMediaLibraryIntegration } from "../../MediaLibrary/MediaSourcePicker";
import {
  getSectionRules,
  getSectionNameFromOptions,
  getClearedValuesForHiddenFields,
  clearErrorsForHiddenFields,
  isFieldHidden,
  isFieldRequired,
  isEventSettingsVisible,
} from "./articleSectionRulesUtils";
import "./article-form.css";

const INITIAL_FORM_STATE = {
  sectionId: "",
  subSectionId: "",
  articleTag: "",
  title: "",
  contentSlug: "",
  shortDescription: "",
  fullDescription: "",
  contentImageFile: null,
  contentImagePreview: "",
  contentImageUrl: "",
  bannerImageFile: null,
  bannerImagePreview: "",
  bannerImageUrl: "",
  detailPageUrl: "",
  authorName: "",
  sourceName: "",
  readingTime: "",
  publishedOn: "",
  eventsFrom: "",
  eventsTo: "",
  eventLocation: "",
  isFeatured: false,
  isFeaturedHome: false,
  isPremium: false,
  displayOrder: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
};

const INITIAL_ERRORS_STATE = {
  sectionId: "",
  title: "",
  contentSlug: "",
  fullDescription: "",
  contentImage: "",
  bannerImage: "",
  shortDescription: "",
  articleTag: "",
  detailPageUrl: "",
  displayOrder: "",
  publishedOn: "",
  eventsFrom: "",
  eventsTo: "",
  eventLocation: "",
  metaTitle: "",
  metaDescription: "",
};

const DATETIME_PICKER_OPTIONS = {
  enableTime: true,
  dateFormat: "Y-m-d H:i",
  time_24hr: true,
  monthSelectorType: "static",
};

const mapDropdownOptions = (
  list,
  nameKey,
  pascalNameKey,
  idKey = "id",
  pascalIdKey = "Id",
) =>
  (list || []).map((item) => ({
    value: String(item[idKey] ?? item[pascalIdKey] ?? ""),
    label: item[nameKey] || item[pascalNameKey] || "-",
  }));

export const AddArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [pageLevelAccessUrl, setPageLevelAccessUrl] = useState("");
  const [sections, setSections] = useState([]);
  const [subSections, setSubSections] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subSectionsLoading, setSubSectionsLoading] = useState(false);
  // Per-field Media Library selection, keyed by file field (this session only).
  const [selectedMedia, setSelectedMedia] = useState({});
  const slugManuallyEdited = useRef(false);

  const selectedSectionName = useMemo(
    () => getSectionNameFromOptions(formData.sectionId, sections),
    [formData.sectionId, sections],
  );

  const sectionRules = useMemo(
    () => getSectionRules(selectedSectionName),
    [selectedSectionName],
  );

  const fieldHidden = useCallback(
    (fieldName) => isFieldHidden(fieldName, sectionRules),
    [sectionRules],
  );

  const fieldRequired = useCallback(
    (fieldName) => isFieldRequired(fieldName, sectionRules),
    [sectionRules],
  );

  useEffect(() => {
    if (!formData.sectionId) return;
    const cleared = getClearedValuesForHiddenFields(sectionRules.hiddenFields);
    setFormData((prev) => ({ ...prev, ...cleared }));
    setErrors((prev) =>
      clearErrorsForHiddenFields(prev, sectionRules.hiddenFields),
    );
  }, [formData.sectionId, selectedSectionName]);

  useEffect(() => {
    if (id) {
      setPageLevelAccessUrl("articles/update/:id");
    } else {
      setPageLevelAccessUrl("articles/add");
    }
  }, [id]);

  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const { isSuperAdmin, loading: profileLoading } = useUserProfile();

  useEffect(() => {
    if (pageAccessData) {
      if (id) {
        if (!pageAccessData.editAccess) {
          navigate("/404-error-page");
        }
      } else if (!pageAccessData.addAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, id, navigate]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [sectionRes, topicRes] = await Promise.all([
          getSectionsList(),
          getContentTopicsList(),
        ]);
        const sectionList = sectionRes.data?.result || sectionRes.data || [];
        const topicList = topicRes.data?.result || topicRes.data || [];
        setSections(
          mapDropdownOptions(
            Array.isArray(sectionList) ? sectionList : [],
            "sectionName",
            "SectionName",
          ),
        );
        setTopics(
          mapDropdownOptions(
            Array.isArray(topicList) ? topicList : [],
            "topicName",
            "TopicName",
          ),
        );
      } catch (error) {
        handleErrors(error);
      }
    };
    loadDropdowns();
  }, []);

  const loadSubSections = useCallback(async (sectionId) => {
    if (!sectionId) {
      setSubSections([]);
      return;
    }
    setSubSectionsLoading(true);
    try {
      const response = await getSubSectionsBySectionId(sectionId);
      const list = response.data?.result || response.data || [];
      setSubSections(
        mapDropdownOptions(
          Array.isArray(list) ? list : [],
          "subSectionName",
          "SubSectionName",
        ),
      );
    } catch (error) {
      handleErrors(error);
      setSubSections([]);
    } finally {
      setSubSectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formData.sectionId) {
      loadSubSections(formData.sectionId);
    } else {
      setSubSections([]);
    }
  }, [formData.sectionId, loadSubSections]);

  useEffect(() => {
    const fetchArticle = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await getArticleById(id);
          const mapped = mapArticleResponse(data);
          setFormData(mapped);
          slugManuallyEdited.current = true;
          await loadSubSections(mapped.sectionId);
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        slugManuallyEdited.current = false;
      }
    };
    fetchArticle();
  }, [id, loadSubSections]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "sectionId") {
      const label = getSectionNameFromOptions(value, sections);
      const rules = getSectionRules(label);
      const cleared = getClearedValuesForHiddenFields(rules.hiddenFields);
      setFormData((prev) => ({
        ...prev,
        sectionId: value,
        subSectionId: "",
        ...cleared,
      }));
      setErrors((prev) =>
        clearErrorsForHiddenFields(
          { ...prev, sectionId: "" },
          rules.hiddenFields,
        ),
      );
      return;
    }
    if (name === "title") {
      setFormData((prev) => ({
        ...prev,
        title: value,
        ...(slugManuallyEdited.current
          ? {}
          : { contentSlug: generateSlug(value) }),
      }));
    } else if (name === "contentSlug") {
      slugManuallyEdited.current = true;
      setFormData((prev) => ({ ...prev, contentSlug: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleEditorChange = useCallback((content) => {
    setFormData((prev) => ({
      ...prev,
      fullDescription: content ?? "",
    }));
    setErrors((prev) => ({ ...prev, fullDescription: "" }));
  }, []);

  const handleDateChange = (name, dates, dateStr) => {
    const value = dateStr || "";
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMediaSelect = (media, fieldKey, previewKey, errorKey, urlKey) => {
    const url = media?.mediaUrl || media?.thumbnailUrl || "";
    if (!url) return;
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: null,
      [previewKey]: url,
      [urlKey]: url,
    }));
    setSelectedMedia((prev) => ({ ...prev, [fieldKey]: media }));
    setErrors((prev) => ({ ...prev, [errorKey]: "" }));
  };

  // Remove only the Media Library selection for a given field.
  const handleMediaRemove = (fieldKey, previewKey, urlKey, errorKey) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: null,
      [previewKey]: "",
      [urlKey]: "",
    }));
    setSelectedMedia((prev) => ({ ...prev, [fieldKey]: null }));
    if (errorKey) {
      setErrors((prev) => ({ ...prev, [errorKey]: "" }));
    }
  };

  const contentImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia.contentImageFile,
    imageUrl: formData.contentImageUrl,
    defaultKeyword: formData.title,
    onSelect: (media) =>
      handleMediaSelect(
        media,
        "contentImageFile",
        "contentImagePreview",
        "contentImage",
        "contentImageUrl",
      ),
    onRemove: () =>
      handleMediaRemove(
        "contentImageFile",
        "contentImagePreview",
        "contentImageUrl",
        "contentImage",
      ),
  });

  const bannerImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia.bannerImageFile,
    imageUrl: formData.bannerImageUrl,
    defaultKeyword: formData.title,
    onSelect: (media) =>
      handleMediaSelect(
        media,
        "bannerImageFile",
        "bannerImagePreview",
        "bannerImage",
        "bannerImageUrl",
      ),
    onRemove: () =>
      handleMediaRemove(
        "bannerImageFile",
        "bannerImagePreview",
        "bannerImageUrl",
        "bannerImage",
      ),
  });

  const handleSave = useCallback(
    async (status) => {
      const { valid, errors: validationErrors } = validateArticle(
        formData,
        isEditMode,
        sectionRules,
      );
      setErrors(validationErrors);
      if (!valid) return;

      const payload = buildArticleFormDataForSubmit(
        formData,
        isEditMode,
        id,
        status,
        sections,
      );

      try {
        setIsButtonDisabled(true);
        const action = isEditMode ? updateArticle : addArticle;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again.",
          );
          return;
        }

        toast.success(
          isSuperAdmin
            ? status === ARTICLE_STATUS.ACTIVE
              ? "Article published successfully!"
              : "Article saved as draft successfully!"
            : "Article submitted for review successfully!",
        );
        setTimeout(() => {
          navigate("/articles");
        }, 1500);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, isEditMode, id, navigate, sections, sectionRules, isSuperAdmin],
  );

  if (loading && isEditMode) {
    return <FormSkeleton />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">
              {isEditMode ? "Update Article" : "Add Article"}
            </h4>
            <div className="page-title-right d-flex align-items-center">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/articles">Articles</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? `Update #${id}` : "Add"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <form
        className="article-form"
        onSubmit={(e) => {
          e.preventDefault();
        }}
        method="POST"
      >
        <div className="row">
          <div className="col-lg-8">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-article-line"></i>
                  </span>
                  Basic Information
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="sectionId" className="form-label">
                        Section <span className="required-field">*</span>
                      </label>
                      <select
                        name="sectionId"
                        id="sectionId"
                        value={formData.sectionId}
                        onChange={handleInputChange}
                        className={`form-select ${
                          errors.sectionId ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select section</option>
                        {sections.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.sectionId && (
                        <div className="invalid-feedback">
                          {errors.sectionId}
                        </div>
                      )}
                    </div>
                  </div>

                  {!fieldHidden("subSectionId") ? (
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor="subSectionId" className="form-label">
                          Sub Section
                        </label>
                        <select
                          name="subSectionId"
                          id="subSectionId"
                          value={formData.subSectionId}
                          onChange={handleInputChange}
                          className="form-select"
                          disabled={!formData.sectionId || subSectionsLoading}
                        >
                          <option value="">
                            {subSectionsLoading
                              ? "Loading…"
                              : "Select sub section (optional)"}
                          </option>
                          {subSections.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : null}

                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="articleTag" className="form-label">
                        Tag
                        {fieldRequired("articleTag") ? (
                          <span className="required-field"> *</span>
                        ) : null}
                      </label>
                      <select
                        name="articleTag"
                        id="articleTag"
                        value={formData.articleTag}
                        onChange={handleInputChange}
                        className={`form-select ${
                          errors.articleTag ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">
                          {fieldRequired("articleTag")
                            ? "Select tag"
                            : "Select tag (optional)"}
                        </option>
                        {topics.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.articleTag && (
                        <div className="invalid-feedback">
                          {errors.articleTag}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="title" className="form-label">
                        Title <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        id="title"
                        placeholder="Enter article title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.title ? "is-invalid" : ""
                        }`}
                      />
                      {errors.title && (
                        <div className="invalid-feedback">{errors.title}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="contentSlug" className="form-label">
                        Content Slug <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="contentSlug"
                        id="contentSlug"
                        placeholder="article-slug"
                        value={formData.contentSlug}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.contentSlug ? "is-invalid" : ""
                        }`}
                      />
                      {errors.contentSlug && (
                        <div className="invalid-feedback">
                          {errors.contentSlug}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="shortDescription" className="form-label">
                        Short Description{" "}
                        <span className="required-field">*</span>
                      </label>
                      <textarea
                        name="shortDescription"
                        id="shortDescription"
                        rows="4"
                        value={formData.shortDescription}
                        onChange={handleInputChange}
                        maxLength={250}
                        className={`form-control ${
                          errors.shortDescription ? "is-invalid" : ""
                        }`}
                      />
                      {errors.shortDescription && (
                        <div className="invalid-feedback">
                          {errors.shortDescription}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-0">
                      <label htmlFor="fullDescription" className="form-label">
                        Full Description{" "}
                        <span className="required-field">*</span>
                      </label>
                      <div className="editor-wrap">
                        <RichTextEditor
                          value={formData.fullDescription}
                          onEditorChange={handleEditorChange}
                          defaultKeyword={formData.title}
                        />
                      </div>
                      {errors.fullDescription && (
                        <div
                          className="text-danger mt-1"
                          style={{ fontSize: ".875em" }}
                        >
                          {errors.fullDescription}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!fieldHidden("contentImage") || !fieldHidden("bannerImage") ? (
              <div className="card mt-3">
                <div className="card-header">
                  <h5 className="card-section-title">
                    <span className="card-section-title__icon">
                      <i className="ri-image-line"></i>
                    </span>
                    Media
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    {!fieldHidden("contentImage") ? (
                      <div
                        className={
                          fieldHidden("bannerImage") ? "col-12" : "col-md-6"
                        }
                      >
                        <ImageUpload
                          label={
                            <>
                              Content Image
                              {fieldRequired("contentImage") ? (
                                <span className="required-field"> *</span>
                              ) : null}
                              <span
                                className="text-muted fw-normal"
                                style={{ fontSize: "0.8125rem" }}
                              >
                                {" "}
                                (Recommended: 950 × 520 px)
                              </span>
                            </>
                          }
                          required={false}
                          previewUrl={formData.contentImagePreview}
                          error={errors.contentImage}
                          {...contentImageLibrary.uploadEnhancements}
                        />
                        {contentImageLibrary.modalSlot}
                      </div>
                    ) : null}
                    {!fieldHidden("bannerImage") ? (
                      <div
                        className={
                          fieldHidden("contentImage") ? "col-12" : "col-md-6"
                        }
                      >
                        <ImageUpload
                          label={
                            <>
                              Banner Image
                              {fieldRequired("bannerImage") ? (
                                <span className="required-field"> *</span>
                              ) : null}
                              <span
                                className="text-muted fw-normal"
                                style={{ fontSize: "0.8125rem" }}
                              >
                                {" "}
                                (Recommended: 1580 × 400 px)
                              </span>
                            </>
                          }
                          required={false}
                          previewUrl={formData.bannerImagePreview}
                          error={errors.bannerImage}
                          {...bannerImageLibrary.uploadEnhancements}
                        />
                        {bannerImageLibrary.modalSlot}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="accordion custom-accordionwithicon accordion-flush mt-3">
              <div className="accordion-item">
                <h2 className="accordion-header" id="articleSeoHeading">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#articleSeoCollapse"
                    aria-expanded="false"
                    aria-controls="articleSeoCollapse"
                  >
                    <i className="ri-search-eye-line me-2"></i>
                    SEO Settings
                  </button>
                </h2>
                <div
                  id="articleSeoCollapse"
                  className="accordion-collapse collapse"
                  aria-labelledby="articleSeoHeading"
                >
                  <div className="accordion-body">
                    <div className="mb-3">
                      <label htmlFor="metaTitle" className="form-label">
                        Meta Title
                      </label>
                      <input
                        type="text"
                        name="metaTitle"
                        id="metaTitle"
                        value={formData.metaTitle}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.metaTitle ? "is-invalid" : ""
                        }`}
                      />
                      {errors.metaTitle && (
                        <div className="invalid-feedback">
                          {errors.metaTitle}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="metaDescription" className="form-label">
                        Meta Description
                      </label>
                      <textarea
                        name="metaDescription"
                        id="metaDescription"
                        rows="4"
                        value={formData.metaDescription}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.metaDescription ? "is-invalid" : ""
                        }`}
                      />
                    </div>
                    <div className="mb-0">
                      <label htmlFor="metaKeywords" className="form-label">
                        Meta Keywords
                      </label>
                      <textarea
                        name="metaKeywords"
                        id="metaKeywords"
                        rows="3"
                        value={formData.metaKeywords}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-settings-3-line"></i>
                  </span>
                  Display Settings
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label d-block">Is Featured</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeatured"
                      name="isFeatured"
                      checked={formData.isFeatured}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label d-block">Featured Home</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeaturedHome"
                      name="isFeaturedHome"
                      checked={formData.isFeaturedHome}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label d-block">Is Premium</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isPremium"
                      name="isPremium"
                      checked={formData.isPremium}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="displayOrder" className="form-label">
                    Display Order{" "}
                    <span className="text-muted fw-normal">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    id="displayOrder"
                    min="1"
                    step="1"
                    value={formData.displayOrder}
                    onChange={handleDisplayOrderChange}
                    onKeyDown={handleDisplayOrderKeyDown}
                    className={`form-control ${
                      errors.displayOrder ? "is-invalid" : ""
                    }`}
                  />
                  {errors.displayOrder && (
                    <div className="invalid-feedback">
                      {errors.displayOrder}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h5 className="card-section-title">
                  <span className="card-section-title__icon">
                    <i className="ri-send-plane-line"></i>
                  </span>
                  Publishing
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label htmlFor="publishedOn" className="form-label">
                        Published On <span className="required-field">*</span>
                      </label>
                      <Flatpickr
                        className={`form-control ${
                          errors.publishedOn ? "is-invalid" : ""
                        }`}
                        placeholder="Select date & time"
                        value={formData.publishedOn}
                        options={DATETIME_PICKER_OPTIONS}
                        onChange={(dates, dateStr) =>
                          handleDateChange("publishedOn", dates, dateStr)
                        }
                      />
                      {errors.publishedOn && (
                        <div className="invalid-feedback d-block">
                          {errors.publishedOn}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label htmlFor="authorName" className="form-label">
                        Author Name <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="authorName"
                        id="authorName"
                        value={formData.authorName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.authorName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.authorName && (
                        <div className="invalid-feedback">
                          {errors.authorName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label htmlFor="sourceName" className="form-label">
                        Source Name <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="sourceName"
                        id="sourceName"
                        value={formData.sourceName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.sourceName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.sourceName && (
                        <div className="invalid-feedback">
                          {errors.sourceName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label htmlFor="readingTime" className="form-label">
                        Reading Time <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="readingTime"
                        id="readingTime"
                        placeholder="e.g. 5 min read"
                        value={formData.readingTime}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.readingTime ? "is-invalid" : ""
                        }`}
                      />
                      {errors.readingTime && (
                        <div className="invalid-feedback">
                          {errors.readingTime}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {isEventSettingsVisible(sectionRules) ? (
              <div className="card mt-3">
                <div className="card-header">
                  <h5 className="card-section-title">
                    <span className="card-section-title__icon">
                      <i className="ri-calendar-event-line"></i>
                    </span>
                    Event Settings
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {!fieldHidden("eventsFrom") ? (
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label htmlFor="eventsFrom" className="form-label">
                            Events From
                            {fieldRequired("eventsFrom") ? (
                              <span className="required-field"> *</span>
                            ) : null}
                          </label>
                          <Flatpickr
                            className={`form-control ${
                              errors.eventsFrom ? "is-invalid" : ""
                            }`}
                            placeholder="Start date & time"
                            value={formData.eventsFrom}
                            options={DATETIME_PICKER_OPTIONS}
                            onChange={(dates, dateStr) =>
                              handleDateChange("eventsFrom", dates, dateStr)
                            }
                          />
                          {errors.eventsFrom && (
                            <div className="invalid-feedback d-block">
                              {errors.eventsFrom}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                    {!fieldHidden("eventsTo") ? (
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label htmlFor="eventsTo" className="form-label">
                            Events To
                            {fieldRequired("eventsTo") ? (
                              <span className="required-field"> *</span>
                            ) : null}
                          </label>
                          <Flatpickr
                            className={`form-control ${
                              errors.eventsTo ? "is-invalid" : ""
                            }`}
                            placeholder="End date & time"
                            value={formData.eventsTo}
                            options={DATETIME_PICKER_OPTIONS}
                            onChange={(dates, dateStr) =>
                              handleDateChange("eventsTo", dates, dateStr)
                            }
                          />
                          {errors.eventsTo && (
                            <div className="invalid-feedback d-block">
                              {errors.eventsTo}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                    {!fieldHidden("eventLocation") ? (
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label htmlFor="eventLocation" className="form-label">
                            Event Location
                            {fieldRequired("eventLocation") ? (
                              <span className="required-field"> *</span>
                            ) : null}
                          </label>
                          <input
                            type="text"
                            name="eventLocation"
                            id="eventLocation"
                            value={formData.eventLocation}
                            onChange={handleInputChange}
                            className={`form-control ${
                              errors.eventLocation ? "is-invalid" : ""
                            }`}
                          />
                          {errors.eventLocation && (
                            <div className="invalid-feedback">
                              {errors.eventLocation}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <div className="form-actions">
              {isSuperAdmin ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary btn-action-primary"
                    disabled={isButtonDisabled || profileLoading}
                    onClick={() => handleSave(ARTICLE_STATUS.ACTIVE)}
                  >
                    {isButtonDisabled ? (
                      <ButtonLoader label="Publishing" />
                    ) : (
                      "Publish"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    disabled={isButtonDisabled || profileLoading}
                    onClick={() => handleSave(ARTICLE_STATUS.DRAFT)}
                  >
                    {isButtonDisabled ? (
                      <ButtonLoader label="Saving" />
                    ) : (
                      "Save Draft"
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-action-primary"
                  disabled={isButtonDisabled || profileLoading}
                  onClick={() => handleSave(ARTICLE_STATUS.DRAFT)}
                >
                  {isButtonDisabled ? (
                    <ButtonLoader label="Submitting" />
                  ) : (
                    "Submit For Review"
                  )}
                </button>
              )}
              <Link to="/articles" className="btn btn-danger">
                Cancel
              </Link>
            </div>
          </div>
        </div>
      </form>

      <AiAssistantOffcanvas
        pageLink={
          pageLevelAccessUrl ||
          (isEditMode ? "articles/update/:id" : "articles/add")
        }
        formData={formData}
        setFormData={setFormData}
        fieldMap={CONTENT_AI_RESPONSE_FIELD_MAP}
        editorFields={["fullDescription"]}
        editorHandlers={{ fullDescription: handleEditorChange }}
      />
    </>
  );
};
